import { useMemo } from 'react'
import * as THREE from 'three'
import { useScene } from '../../scene/store'

/**
 * De vlek van de contactschaduw: een afgeronde rechthoek die zacht uitloopt,
 * gedeeld door alle trucks. Volle dekking tot 35 % van de halve maat, dan
 * vloeiend naar nul aan de rand van het vlak.
 */
let kaart: THREE.DataTexture | null = null
function schaduwKaart(): THREE.DataTexture {
  if (kaart) return kaart
  const n = 64
  const data = new Uint8Array(n * n * 4)
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const u = Math.abs(((x + 0.5) / n) * 2 - 1)
      const v = Math.abs(((y + 0.5) / n) * 2 - 1)
      const r = Math.pow(u ** 4 + v ** 4, 0.25)
      const a = Math.pow(1 - THREE.MathUtils.smoothstep(r, 0.35, 1), 1.6)
      data[(y * n + x) * 4 + 3] = Math.round(a * 255)
    }
  }
  kaart = new THREE.DataTexture(data, n, n)
  kaart.needsUpdate = true
  return kaart
}

/**
 * Contactschaduw onder een truck, als kind van zijn groep zodat hij meerijdt
 * en meedraait (21 oktober).
 *
 * Zonder dit stonden de trucks los van de vloer: alleen de zon werpt
 * schaduw, en dat alleen onder de daklichten; de ambient occlusion deed de
 * rest, en die staat in het lichte prestatieprofiel uit. Een zwevende machine
 * is een van de eerste dingen waaraan je een render herkent. Eén vlak en één
 * gedeelde textuur per truck: dat kost in beide profielen niets.
 *
 * Met AO aan is hij lichter, want dan doet de AO de rand al.
 */
export function Contactschaduw({ breedte, lengte, z = 0, sterkte = 1 }: {
  /** Maat van de voetafdruk, dwars (x) en langs (z), in meters. */
  breedte: number
  lengte: number
  z?: number
  sterkte?: number
}) {
  const ao = useScene((s) => s.scene.effecten.ao)
  const map = useMemo(schaduwKaart, [])
  return (
    <mesh
      position={[0, 0.008, z]}
      rotation={[-Math.PI / 2, 0, 0]}
      // Het vlak is ruimer dan de voetafdruk: de zachte rand valt erbuiten.
      scale={[breedte * 1.3, lengte * 1.3, 1]}
      renderOrder={3}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial
        map={map}
        color="#000000"
        transparent
        opacity={(ao ? 0.35 : 0.6) * sterkte}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-4}
        polygonOffsetUnits={-4}
      />
    </mesh>
  )
}
